import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { Opcion, Pagina, Proveedor } from '../api/tipos';
import { useSesion } from '../sesion';
import {
  Cargando, Dialogo, DialogoMotivo, Error, Insignia, Paginacion,
} from '../componentes/comunes';

/**
 * Proveedores.
 *
 * El listado muestra siempre la cuenta enmascarada. Ver una cuenta
 * completa exige abrir la ficha y tener el permiso de datos bancarios, y
 * queda registrado quién lo hizo: es un dato que sirve para desviar
 * dinero, no sólo para consultar.
 */
export default function Proveedores() {
  const { puede } = useSesion();

  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('');
  const [bancarios, setBancarios] = useState('');
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<Proveedor> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [bancos, setBancos] = useState<Opcion[]>([]);
  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<Proveedor | null>(null);
  const [viendo, setViendo] = useState<Proveedor | null>(null);
  const [cambiandoEstado, setCambiandoEstado] = useState<Proveedor | null>(null);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/bancos/opciones').then(setBancos).catch(() => undefined);
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Proveedor>>('/proveedores', {
      busqueda: busqueda || undefined,
      estado: estado || undefined,
      conDatosBancarios: bancarios || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, estado, bancarios, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Proveedores</h1>
          <p className="sub">
            Quién surte las refacciones, con sus condiciones de pago y sus
            datos bancarios cifrados.
          </p>
        </div>
        {puede('proveedores', 'crear') && (
          <button className="primario" onClick={() => setCreando(true)}>Nuevo proveedor</button>
        )}
      </div>

      <div className="panel">
        <div className="controles">
          <input
            type="search"
            placeholder="Código, nombre, RFC o contacto…"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
          />
          <select value={estado} onChange={(e) => { setEstado(e.target.value); setNumero(1); }}>
            <option value="">Todos</option>
            <option value="activo">Sólo activos</option>
            <option value="inactivo">Sólo inactivos</option>
          </select>
          <select
            value={bancarios}
            onChange={(e) => { setBancarios(e.target.value); setNumero(1); }}
          >
            <option value="">Con y sin datos bancarios</option>
            <option value="si">Con datos bancarios</option>
            <option value="no">Sin datos bancarios</option>
          </select>
        </div>

        <Error error={error} />

        {cargando && !pagina ? <Cargando /> : pagina && (
          <>
            {pagina.data.length === 0 ? (
              <div className="vacio">No hay proveedores que cumplan esos filtros.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>RFC</th>
                    <th>Contacto</th>
                    <th>Banco</th>
                    <th>CLABE</th>
                    <th className="numero">Crédito</th>
                    <th>Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {pagina.data.map((p) => (
                    <tr key={p.id}>
                      <td className="mono">{p.codigo}</td>
                      <td>
                        <button className="enlace" onClick={() => setViendo(p)}>{p.nombre}</button>
                      </td>
                      <td className="mono">{p.rfc ?? '—'}</td>
                      <td>{p.contacto ?? '—'}</td>
                      <td>{p.banco?.nombre ?? '—'}</td>
                      <td className="mono">{p.clabe ?? '—'}</td>
                      <td className="numero">
                        {p.diasCredito === null ? '—' : `${p.diasCredito} d`}
                      </td>
                      <td><Insignia activo={p.activo} /></td>
                      <td className="acciones">
                        {puede('proveedores', 'editar') && (
                          <>
                            <button className="enlace" onClick={() => setEditando(p)}>Editar</button>
                            <button className="enlace" onClick={() => setCambiandoEstado(p)}>
                              {p.activo ? 'Desactivar' : 'Reactivar'}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <Paginacion
              pagina={pagina.pagina}
              limite={pagina.limite}
              total={pagina.total}
              onCambiar={setNumero}
            />
          </>
        )}
      </div>

      {viendo && <Ficha id={viendo.id} onCerrar={() => setViendo(null)} />}

      {(creando || editando) && (
        <FormularioProveedor
          bancos={bancos}
          proveedor={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
          onGuardado={() => { setCreando(false); setEditando(null); cargar(); }}
        />
      )}

      {cambiandoEstado && (
        <CambioEstadoProveedor
          proveedor={cambiandoEstado}
          onCerrar={() => setCambiandoEstado(null)}
          onHecho={() => { setCambiandoEstado(null); cargar(); }}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════ ficha

function Ficha({ id, onCerrar }: { id: string; onCerrar: () => void }) {
  const [proveedor, setProveedor] = useState<Proveedor | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<Proveedor>(`/proveedores/${id}`).then(setProveedor).catch(setError);
  }, [id]);

  return (
    <Dialogo
      titulo={proveedor ? `${proveedor.codigo} — ${proveedor.nombre}` : 'Proveedor'}
      onCerrar={onCerrar}
      pie={<button onClick={onCerrar}>Cerrar</button>}
    >
      <Error error={error} />
      {!proveedor ? <Cargando /> : (
        <>
          <Dato etiqueta="RFC" valor={proveedor.rfc} mono />
          <Dato etiqueta="Contacto" valor={proveedor.contacto} />
          <Dato etiqueta="Correo" valor={proveedor.correo} />
          <Dato etiqueta="Teléfono" valor={proveedor.telefono} />
          <Dato etiqueta="Dirección" valor={proveedor.direccionCompleta} />
          <Dato
            etiqueta="Días de crédito"
            valor={proveedor.diasCredito === null ? null : String(proveedor.diasCredito)}
          />

          <h2>Datos bancarios</h2>
          {!proveedor.tieneDatosBancarios ? (
            <div className="aviso">Este proveedor todavía no tiene cuenta capturada.</div>
          ) : (
            <>
              {!proveedor.datosBancariosVisibles && (
                <div className="aviso">
                  Se muestran enmascarados: tu perfil no tiene el permiso de
                  ver datos bancarios.
                </div>
              )}
              <Dato etiqueta="Banco" valor={proveedor.banco?.nombre ?? null} />
              <Dato etiqueta="Titular" valor={proveedor.titularCuenta} />
              <Dato etiqueta="Cuenta" valor={proveedor.numeroCuenta} mono />
              <Dato etiqueta="CLABE" valor={proveedor.clabe} mono />
              {proveedor.datosBancariosVisibles && (
                <div className="aviso">
                  Esta consulta quedó registrada en la bitácora de datos sensibles.
                </div>
              )}
            </>
          )}
        </>
      )}
    </Dialogo>
  );
}

function Dato({ etiqueta, valor, mono }: { etiqueta: string; valor: string | null; mono?: boolean }) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      <span className={mono ? 'mono' : undefined}>{valor ?? '—'}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════ formulario

function FormularioProveedor({
  bancos, proveedor, onCerrar, onGuardado,
}: {
  bancos: Opcion[];
  proveedor: Proveedor | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const esAlta = proveedor === null;
  const [datos, setDatos] = useState({
    codigo: proveedor?.codigo ?? '',
    nombre: proveedor?.nombre ?? '',
    rfc: proveedor?.rfc ?? '',
    correo: proveedor?.correo ?? '',
    telefono: proveedor?.telefono ?? '',
    direccionCompleta: proveedor?.direccionCompleta ?? '',
    contacto: proveedor?.contacto ?? '',
    bancoId: proveedor?.banco?.id ?? '',
    titularCuenta: proveedor?.titularCuenta ?? '',
    diasCredito: proveedor?.diasCredito === null ? '' : String(proveedor?.diasCredito ?? ''),
    numeroCuenta: '',
    clabe: '',
  });
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      const cuerpo: Record<string, unknown> = {
        nombre: datos.nombre.trim(),
        rfc: datos.rfc.trim() || undefined,
        correo: datos.correo.trim() || undefined,
        telefono: datos.telefono.trim() || undefined,
        direccionCompleta: datos.direccionCompleta.trim() || undefined,
        contacto: datos.contacto.trim() || undefined,
        bancoId: datos.bancoId || undefined,
        titularCuenta: datos.titularCuenta.trim() || undefined,
        diasCredito: datos.diasCredito === '' ? undefined : Number(datos.diasCredito),
      };
      if (esAlta) cuerpo.codigo = datos.codigo.trim();

      // Los campos bancarios sólo viajan si se escribió algo: el
      // formulario los abre vacíos a propósito, porque no se descargan
      // en claro, y mandarlos vacíos borraría la cuenta existente.
      if (datos.numeroCuenta.trim()) cuerpo.numeroCuenta = datos.numeroCuenta.trim();
      if (datos.clabe.trim()) cuerpo.clabe = datos.clabe.trim();

      if (esAlta) await api.post('/proveedores', cuerpo);
      else await api.patch(`/proveedores/${proveedor!.id}`, cuerpo);

      onGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={esAlta ? 'Nuevo proveedor' : `Editar ${proveedor!.codigo}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando}>
            {trabajando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      {esAlta && (
        <div className="campo">
          <label htmlFor="codigo">Código *</label>
          <input
            id="codigo" className="mono" value={datos.codigo} maxLength={20}
            onChange={(e) => setDatos({ ...datos, codigo: e.target.value })}
          />
        </div>
      )}

      <div className="campo">
        <label htmlFor="nombre">Nombre o razón social *</label>
        <input
          id="nombre" value={datos.nombre} maxLength={250}
          onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="rfc">RFC</label>
        <input
          id="rfc" className="mono" value={datos.rfc} maxLength={13}
          onChange={(e) => setDatos({ ...datos, rfc: e.target.value.toUpperCase() })}
        />
      </div>

      <div className="campo">
        <label htmlFor="contacto">Contacto</label>
        <input
          id="contacto" value={datos.contacto} maxLength={150}
          onChange={(e) => setDatos({ ...datos, contacto: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="correo">Correo</label>
        <input
          id="correo" type="email" value={datos.correo} maxLength={150}
          onChange={(e) => setDatos({ ...datos, correo: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="telefono">Teléfono</label>
        <input
          id="telefono" value={datos.telefono} maxLength={30}
          onChange={(e) => setDatos({ ...datos, telefono: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="direccion">Dirección</label>
        <textarea
          id="direccion" rows={2} value={datos.direccionCompleta} maxLength={300}
          onChange={(e) => setDatos({ ...datos, direccionCompleta: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="diasCredito">Días de crédito</label>
        <input
          id="diasCredito" type="number" min={0} max={365} value={datos.diasCredito}
          onChange={(e) => setDatos({ ...datos, diasCredito: e.target.value })}
        />
      </div>

      <h2>Datos bancarios</h2>

      <div className="campo">
        <label htmlFor="bancoId">Banco</label>
        <select
          id="bancoId" value={datos.bancoId}
          onChange={(e) => setDatos({ ...datos, bancoId: e.target.value })}
        >
          <option value="">— sin asignar —</option>
          {bancos.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
        </select>
      </div>

      <div className="campo">
        <label htmlFor="titular">Titular de la cuenta</label>
        <input
          id="titular" value={datos.titularCuenta} maxLength={200}
          onChange={(e) => setDatos({ ...datos, titularCuenta: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="numeroCuenta">Número de cuenta</label>
        <input
          id="numeroCuenta" className="mono" value={datos.numeroCuenta} maxLength={30}
          placeholder={esAlta ? '' : 'Escribe sólo si quieres reemplazarla'}
          onChange={(e) => setDatos({ ...datos, numeroCuenta: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="clabe">CLABE</label>
        <input
          id="clabe" className="mono" value={datos.clabe} maxLength={25}
          placeholder={esAlta ? '18 dígitos' : 'Escribe sólo si quieres reemplazarla'}
          onChange={(e) => setDatos({ ...datos, clabe: e.target.value })}
        />
        <span className="ayuda">
          Se comprueba el dígito verificador antes de guardar. Una CLABE mal
          capturada no falla al guardar, falla el día de la transferencia.
        </span>
      </div>
    </Dialogo>
  );
}

function CambioEstadoProveedor({
  proveedor, onCerrar, onHecho,
}: {
  proveedor: Proveedor;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      const accion = proveedor.activo ? 'desactivar' : 'reactivar';
      await api.post(`/proveedores/${proveedor.id}/${accion}`, { motivo });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <DialogoMotivo
      titulo={`${proveedor.activo ? 'Desactivar' : 'Reactivar'} proveedor`}
      textoBoton={proveedor.activo ? 'Desactivar' : 'Reactivar'}
      motivo={motivo}
      setMotivo={setMotivo}
      trabajando={trabajando}
      error={error}
      onConfirmar={confirmar}
      onCerrar={onCerrar}
      descripcion={
        <p>
          <strong>{proveedor.codigo} — {proveedor.nombre}</strong>
          {proveedor.activo
            ? ' dejará de ofrecerse al generar órdenes de compra.'
            : ' volverá a estar disponible.'}
        </p>
      }
    />
  );
}
